import api from './api';
import type { WorkOrderRecipeDTO } from './workOrderRecipeService';
import { normalizeListResponse } from '../utils/listResponse';

export interface ArtisanProfileListItemDTO {
    id: string;
    title: string;
    roaster: string;
    machine: string;
    roast_date: string | null;
    roast_time: string;
    is_synced: boolean;
    work_order_recipe_id?: string;
    work_order_number?: string;
    has_raw_file: boolean;
    created_at: string;
}

export interface ArtisanProfileDTO extends ArtisanProfileListItemDTO {
    company_id: string;
    drum_speed: string;
    roast_uuid: string;
    created_by?: string;
    updated_at: string;
    raw_file_name?: string;
    raw_file_size?: number;
    recipe?: WorkOrderRecipeDTO;
}

export interface CreateArtisanProfileRequest {
    title: string;
    roaster: string;
    machine: string;
    roast_date: string;
    roast_time: string;
    drum_speed: string;
    roast_uuid: string;
    recipe: Partial<WorkOrderRecipeDTO>;
}

export interface BulkDeleteResult {
    id: string;
    status: 'deleted' | 'blocked' | 'not_found' | 'failed';
    blocked_by?: string;
    message?: string;
}

export interface BulkDeleteResponse {
    results: BulkDeleteResult[];
    deleted: number;
    blocked: number;
}

export interface ArtisanProfileFilters {
    status?: string; // "all", "synced", "unsynced"
    roaster?: string;
    machine?: string;
    date_from?: string;
    date_to?: string;
}

/**
 * Declared on every FormData post, matching companyService.uploadLogo. The shared axios
 * instance defaults Content-Type to application/json, and axios v1 serialises FormData
 * into a JSON object whenever that header is already JSON — the files never leave the
 * browser and the backend answers "expected a multipart upload". api.ts now clears the
 * header for FormData bodies, which is the real guard; stating multipart here means these
 * two calls survive on their own if that ever changes. Either way the browser replaces
 * this value with the real boundary before the request goes out.
 */
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

export const artisanProfileService = {
    async getAll(filters?: ArtisanProfileFilters): Promise<ArtisanProfileListItemDTO[]> {
        const params = new URLSearchParams();
        if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
        if (filters?.roaster) params.append('roaster', filters.roaster);
        if (filters?.machine) params.append('machine', filters.machine);
        if (filters?.date_from) params.append('date_from', filters.date_from);
        if (filters?.date_to) params.append('date_to', filters.date_to);

        const response = await api.get<ArtisanProfileListItemDTO[]>(`/artisan-profiles?${params.toString()}`);
        return response.data;
    },

    async list(filters: ArtisanProfileFilters & { page?: number; size?: number } = {}): Promise<{ data: ArtisanProfileListItemDTO[]; total: number }> {
        const { page = 1, size = 25, ...rest } = filters;
        const params = new URLSearchParams();
        params.set('limit', String(size));
        params.set('offset', String((page - 1) * size));
        params.set('include_meta', 'true');
        if (rest.status && rest.status !== 'all') params.append('status', rest.status);
        if (rest.roaster) params.append('roaster', rest.roaster);
        if (rest.machine) params.append('machine', rest.machine);
        if (rest.date_from) params.append('date_from', rest.date_from);
        if (rest.date_to) params.append('date_to', rest.date_to);
        const response = await api.get(`/artisan-profiles?${params.toString()}`);
        const normalized = normalizeListResponse<ArtisanProfileListItemDTO>(response.data);
        return { data: normalized.data, total: normalized.total };
    },

    async getById(id: string): Promise<ArtisanProfileDTO> {
        const response = await api.get<ArtisanProfileDTO>(`/artisan-profiles/${id}`);
        return response.data;
    },

    async upload(payload: CreateArtisanProfileRequest): Promise<ArtisanProfileDTO> {
        const response = await api.post<ArtisanProfileDTO>('/artisan-profiles', payload);
        return response.data;
    },

    /**
     * Uploads one reviewed profile together with the file it came from, so the roaster's
     * preview edits are kept and the original stays downloadable. A batch is a sequence of
     * these — one request per roast — rather than POST /artisan-profiles/bulk, which parses
     * server-side and would discard whatever was corrected in the preview.
     */
    async uploadWithFile(payload: CreateArtisanProfileRequest, file: File): Promise<ArtisanProfileDTO> {
        const form = new FormData();
        form.append('file', file);
        form.append('payload', JSON.stringify(payload));
        const response = await api.post<ArtisanProfileDTO>('/artisan-profiles', form, MULTIPART);
        return response.data;
    },

    async remove(id: string): Promise<void> {
        await api.delete(`/artisan-profiles/${id}`);
    },

    async bulkRemove(ids: string[]): Promise<BulkDeleteResponse> {
        const response = await api.post<BulkDeleteResponse>('/artisan-profiles/bulk-delete', { ids });
        return response.data;
    },

    /** Fetches the stored file as a blob; the endpoint needs the auth header. */
    async downloadFile(id: string): Promise<Blob> {
        const response = await api.get(`/artisan-profiles/${id}/file`, { responseType: 'blob' });
        return response.data as Blob;
    },

    async syncToWorkOrder(id: string, workOrderId: string): Promise<ArtisanProfileDTO> {
        const response = await api.post<ArtisanProfileDTO>(`/artisan-profiles/${id}/sync`, {
            work_order_id: workOrderId,
        });
        return response.data;
    },
};
